import { createFileRoute, Link } from '@tanstack/react-router';

export const Route = createFileRoute('/seasonal')({
  component: SeasonalPage,
  head: () => ({
    meta: [
      { title: 'What\u2019s on this season \u2014 Warren-Blackwood' },
      {
        name: 'description',
        content:
          'A seasonal guide for Warren-Blackwood: summer bushfire prep, autumn harvest worker resources, winter flood and storm awareness, and spring wildflowers and tourism.',
      },
      { property: 'og:title', content: 'Seasonal guide \u2014 Warren-Blackwood' },
      {
        property: 'og:description',
        content:
          'Right information at the right time \u2014 bushfire prep, flood awareness, harvest workers, wildflowers and tourism.',
      },
    ],
  }),
});

type SeasonId = 'summer' | 'autumn' | 'winter' | 'spring';

interface Season {
  id: SeasonId;
  /** Months (1-12) this season spans, inclusive. */
  months: number[];
  label: string;
  /** Short calendar window shown in the chip. */
  months_label: string;
  headline: string;
  tagline: string;
  accent: string; // CSS color token or hex
  /** What's happening right now in this season. */
  whats_on: string;
  /** Practical action items with optional links. */
  actions: { title: string; body: React.ReactNode }[];
}

const accentLink: React.CSSProperties = {
  color: 'var(--rivergum)',
  textDecoration: 'underline',
  textUnderlineOffset: 2,
};

const SEASONS: Season[] = [
  {
    id: 'summer',
    months: [12, 1, 2],
    months_label: 'Dec \u2013 Feb',
    label: 'Summer',
    headline: 'Bushfire season',
    tagline:
      'Fire danger ratings sit at high or above for most of the south-west. Total Fire Bans are common; restricted-burning periods are in force.',
    accent: '#C8542B',
    whats_on:
      'Dry lightning storms, tourist influx along the coast (Margaret River, Augusta, Denmark, Walpole), early stone-fruit and grape harvest in the Manjimup\u2013Pemberton belt, and elevated bushfire risk across forested shires.',
    actions: [
      {
        title: 'Have a written bushfire plan',
        body: (
          <>
            Decide early whether you will leave or stay and defend. The
            Department of Fire and Emergency Services template walks you
            through it.{' '}
            <Link to="/emergency/fire-preparation" style={accentLink}>
              Local fire-preparation page \u2192
            </Link>
            {' \u00b7 '}
            <a
              href="https://www.dfes.wa.gov.au/hazard-information/bushfire/my-bushfire-plan"
              style={accentLink}
            >
              DFES My Bushfire Plan \u2197
            </a>
          </>
        ),
      },
      {
        title: 'Check the fire danger rating daily',
        body: (
          <>
            Ratings change every morning. The new four-level national system:
            Moderate, High, Extreme, Catastrophic.{' '}
            <a href="https://www.emergency.wa.gov.au" style={accentLink}>
              Emergency WA \u2197
            </a>
            {' '}
            for current ratings, warnings, road closures and Total Fire Ban
            declarations.
          </>
        ),
      },
      {
        title: 'Know your local burn restrictions',
        body: (
          <>
            Each shire sets its own Restricted and Prohibited Burning periods.
            Check the dates for your shire on the{' '}
            <Link to="/" style={accentLink}>shire page</Link> for where you
            live, or via the council\u2019s website.
          </>
        ),
      },
      {
        title: 'Plan for the tourist surge',
        body: (
          <>
            Coastal towns swell three- to five-fold over the peak weeks.
            Patient: queues at fuel, IGA, GPs and the hospital are longer
            than usual. Locals: keep emergency-contact and medication
            paperwork to hand if a fire affects evacuation routes.
          </>
        ),
      },
    ],
  },
  {
    id: 'autumn',
    months: [3, 4, 5],
    months_label: 'Mar \u2013 May',
    label: 'Autumn',
    headline: 'Harvest season',
    tagline:
      'Apples, pears, avocados, stone fruit, wine grapes \u2014 the Manjimup\u2013Pemberton\u2013Donnybrook belt employs hundreds of seasonal workers from March through May.',
    accent: '#A0651A',
    whats_on:
      'Apple, pear and avocado harvest peaks; vintage in the Margaret River and Pemberton wine regions; backpackers and Pacific Australia Labour Mobility (PALM) scheme workers in town; school holidays in April; cooler nights returning.',
    actions: [
      {
        title: 'Harvest worker rights and conditions',
        body: (
          <>
            Workers must be paid the Horticulture Award rate (piece-rates are
            allowed but must average to the award).{' '}
            <a href="https://www.fairwork.gov.au/find-help-for/horticulture-showcase" style={accentLink}>
              Fair Work Ombudsman \u2014 Horticulture Showcase \u2197
            </a>{' '}
            \u00b7{' '}
            <a href="https://www.fairwork.gov.au/tools-and-resources/language-help" style={accentLink}>
              Help in 40+ languages \u2197
            </a>
          </>
        ),
      },
      {
        title: 'Finding harvest work',
        body: (
          <>
            <a href="https://harvesttrail.com.au" style={accentLink}>
              Harvest Trail Services (federal) \u2197
            </a>{' '}
            lists current vacancies by region. The Manjimup Harvest Office and
            local CRCs also keep noticeboards.
          </>
        ),
      },
      {
        title: 'PALM scheme employers',
        body: (
          <>
            If you employ Pacific Australia Labour Mobility scheme workers,
            DEWR\u2019s deed and accommodation standards still apply through
            harvest. Refer issues to{' '}
            <a href="https://www.palmscheme.gov.au" style={accentLink}>
              palmscheme.gov.au \u2197
            </a>
            .
          </>
        ),
      },
      {
        title: 'Burning off resumes \u2014 carefully',
        body: (
          <>
            Restricted Burning Periods usually open in late March or April
            (varies by shire). You need a permit and must notify your neighbours
            and your local bush fire brigade or Fire Control Officer before burning. Only call 000 in an emergency. Check{' '}
            <a href="https://www.emergency.wa.gov.au" style={accentLink}>
              Emergency WA \u2197
            </a>{' '}
            for Harvest and Vehicle Movement Bans before lighting any fire.
          </>
        ),
      },
    ],
  },
  {
    id: 'winter',
    months: [6, 7, 8],
    months_label: 'Jun \u2013 Aug',
    label: 'Winter',
    headline: 'Flood and storm season',
    tagline:
      'Heavy rain, wind events and localised flooding on the Blackwood, Donnelly and Warren rivers \u2014 plus storm damage to power and trees.',
    accent: '#2F6F8F',
    whats_on:
      'Peak rainfall and river flows; storm fronts off the Southern Ocean; occasional river flooding around Bridgetown, Nannup and Pemberton; Synergy/Western Power outages; truffle season around Manjimup; school terms 2 and 3.',
    actions: [
      {
        title: 'Sign up to flood and storm warnings',
        body: (
          <>
            <a href="https://www.emergency.wa.gov.au" style={accentLink}>
              Emergency WA \u2197
            </a>{' '}
            publishes severe weather and flood warnings. The Bureau also runs{' '}
            <a href="http://www.bom.gov.au/wa/warnings/" style={accentLink}>
              BoM WA warnings \u2197
            </a>
            . Both work without an account.
          </>
        ),
      },
      {
        title: 'Report power outages and stay safe',
        body: (
          <>
            Western Power faults:{' '}
            <a href="https://www.westernpower.com.au/faults-and-outages" style={accentLink}>
              westernpower.com.au/faults \u2197
            </a>{' '}
            or call <strong>13 13 51</strong>. Stay 8 metres clear of fallen
            powerlines and assume they are live.
          </>
        ),
      },
      {
        title: 'Tree and storm damage \u2014 who fixes what',
        body: (
          <>
            Trees on local roads: report to your shire. Trees on state
            highways (South Western, Brockman, Vasse, Bussell, South Coast):
            <a href="https://www.mainroads.wa.gov.au/contact-us/report-an-incident-or-issue/" style={accentLink}>
              {' '}Main Roads WA \u2014 report an incident \u2197
            </a>{' '}
            or call <strong>138 138</strong>. Trees on your own property are
            yours; SES is for life-threatening situations only.
          </>
        ),
      },
      {
        title: 'Help is available if you are flooded',
        body: (
          <>
            Call <strong>132 500</strong> for SES help with flood damage,
            sandbagging and roof repairs. Disaster Recovery Funding
            Arrangements (DRFA) may apply for declared events \u2014 your
            shire will publicise eligibility.
          </>
        ),
      },
    ],
  },
  {
    id: 'spring',
    months: [9, 10, 11],
    months_label: 'Sep \u2013 Nov',
    label: 'Spring',
    headline: 'Wildflowers, tourism \u2014 and fire prep begins',
    tagline:
      'Wildflower season runs September to November across the south-west. Restricted Burning Periods close, prohibited burning starts, and bushfire-readiness work needs to be done now \u2014 not in December.',
    accent: '#5B7F2F',
    whats_on:
      'Wildflower tourism (Boranup Forest, Bramley, Stirling Range further east); long-weekend visitors over the King\'s Birthday long weekend (late September) and the October school holidays; orchard blossom; cropping and shearing in the agricultural shires; fire-break inspections by shires.',
    actions: [
      {
        title: 'Do your bushfire prep NOW',
        body: (
          <>
            October is the practical deadline. Clear gutters, cut firebreaks,
            move woodpiles away from the house, test the pump, agree your
            family plan.{' '}
            <Link to="/emergency/fire-preparation" style={accentLink}>
              Local fire-preparation page \u2192
            </Link>
          </>
        ),
      },
      {
        title: 'Firebreak notices \u2014 know the deadline',
        body: (
          <>
            Each shire issues an annual firebreak and fuel-hazard reduction
            notice with a compliance deadline (usually late November). Failing
            to comply attracts a fine and the shire can do the work and bill
            you. Check your shire\u2019s notice on the{' '}
            <Link to="/" style={accentLink}>shire page</Link>.
          </>
        ),
      },
      {
        title: 'Wildflower season \u2014 leave no trace',
        body: (
          <>
            Stay on tracks, do not pick wildflowers (it\u2019s illegal under
            the Biodiversity Conservation Act 2016) and check for{' '}
            <a href="https://www.dbca.wa.gov.au" style={accentLink}>
              DBCA Parks WA \u2197
            </a>{' '}
            alerts on dieback hygiene stations.
          </>
        ),
      },
      {
        title: 'Tourism businesses \u2014 staff up early',
        body: (
          <>
            School holidays + long weekends + wildflower tours push regional
            accommodation, cafes and tour operators to 90%+ utilisation. The{' '}
            <Link to="/services/small-business" style={accentLink}>
              small business services page
            </Link>{' '}
            has links to RSL/AHA recruitment supports and the local jobs
            boards.
          </>
        ),
      },
    ],
  },
];

function currentSeason(now: Date = new Date()): Season {
  const m = now.getMonth() + 1;
  return SEASONS.find((s) => s.months.includes(m))!;
}

const card: React.CSSProperties = {
  background: 'var(--cream)',
  border: '1px solid var(--rule)',
  borderRadius: 8,
  padding: '1.1rem 1.2rem',
  marginBottom: 'var(--sp-md)',
};

const chip = (active: boolean, accent: string): React.CSSProperties => ({
  display: 'inline-flex',
  alignItems: 'baseline',
  gap: '0.4rem',
  padding: '0.45rem 0.8rem',
  borderRadius: 999,
  background: active ? accent : 'var(--cream)',
  color: active ? 'white' : 'var(--ink)',
  border: `1px solid ${active ? accent : 'var(--rule)'}`,
  fontFamily: 'var(--font-sub)',
  fontWeight: 700,
  fontSize: '0.82rem',
  textDecoration: 'none',
  minHeight: 44,
  boxSizing: 'border-box',
});

function SeasonalPage() {
  const current = currentSeason();

  return (
    <main
      id="main-content"
      style={{
        maxWidth: 780,
        margin: '0 auto',
        padding: 'var(--sp-lg) var(--sp-md) var(--sp-xl)',
      }}
    >
      <p
        style={{
          fontFamily: 'var(--font-sub)',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          fontSize: '0.72rem',
          color: 'var(--rivergum)',
          margin: '0 0 0.35rem',
        }}
      >
        Seasonal guide
      </p>
      <h1
        style={{
          fontFamily: 'var(--font-heading)',
          fontSize: 'clamp(2rem, 5.5vw, 2.6rem)',
          color: 'var(--green)',
          margin: '0 0 0.5rem',
          lineHeight: 1.1,
        }}
      >
        What\u2019s on this season in Warren-Blackwood
      </h1>
      <p style={{ color: 'var(--ink-mid)', margin: '0 0 var(--sp-md)', fontSize: '1.02rem', lineHeight: 1.55 }}>
        The electorate runs on strong seasonal rhythms \u2014 summer
        bushfires, autumn harvest, winter floods and storms, spring
        wildflowers. The page is anchored to the current month, but every
        season has its own section below.
      </p>

      <nav
        aria-label="Jump to season"
        style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', margin: '0 0 var(--sp-md)' }}
      >
        {SEASONS.map((s) => (
          <a key={s.id} href={`#${s.id}`} style={chip(s.id === current.id, s.accent)}>
            {s.label}
            <span style={{ fontSize: '0.72rem', opacity: 0.85, fontWeight: 500 }}>{s.months_label}</span>
          </a>
        ))}
      </nav>

      <section
        aria-labelledby="now"
        style={{
          ...card,
          background: 'var(--green-pale)',
          borderColor: current.accent,
          borderLeftWidth: 4,
          borderLeftStyle: 'solid',
        }}
      >
        <p
          style={{
            margin: '0 0 0.25rem',
            fontFamily: 'var(--font-sub)',
            fontWeight: 800,
            textTransform: 'uppercase',
            letterSpacing: '0.08em',
            fontSize: '0.72rem',
            color: current.accent,
          }}
        >
          Right now \u00b7 {current.label} \u00b7 {current.months_label}
        </p>
        <h2
          id="now"
          style={{
            fontFamily: 'var(--font-heading)',
            fontSize: '1.5rem',
            color: 'var(--green)',
            margin: '0 0 0.5rem',
          }}
        >
          {current.headline}
        </h2>
        <p style={{ margin: '0 0 0.6rem', fontSize: '1rem', lineHeight: 1.55 }}>
          {current.tagline}
        </p>
        <p style={{ margin: 0, fontSize: '0.94rem', color: 'var(--ink-mid)', lineHeight: 1.55 }}>
          {current.whats_on}
        </p>
      </section>

      {SEASONS.map((s) => (
        <section key={s.id} id={s.id} aria-labelledby={`${s.id}-h`} style={card}>
          <p
            style={{
              margin: '0 0 0.25rem',
              fontFamily: 'var(--font-sub)',
              fontWeight: 800,
              textTransform: 'uppercase',
              letterSpacing: '0.08em',
              fontSize: '0.72rem',
              color: s.accent,
            }}
          >
            {s.label} \u00b7 {s.months_label}
          </p>
          <h2
            id={`${s.id}-h`}
            style={{
              fontFamily: 'var(--font-heading)',
              fontSize: '1.3rem',
              color: 'var(--green)',
              margin: '0 0 0.5rem',
            }}
          >
            {s.headline}
          </h2>
          <p style={{ margin: '0 0 0.7rem', fontSize: '0.97rem', lineHeight: 1.55 }}>
            {s.tagline}
          </p>
          <p style={{ margin: '0 0 0.9rem', fontSize: '0.92rem', color: 'var(--ink-mid)', lineHeight: 1.5 }}>
            <strong style={{ color: 'var(--ink)' }}>What\u2019s happening:</strong> {s.whats_on}
          </p>
          <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'grid', gap: '0.7rem' }}>
            {s.actions.map((a) => (
              <li
                key={a.title}
                style={{
                  background: 'white',
                  border: '1px solid var(--rule)',
                  borderLeft: `3px solid ${s.accent}`,
                  borderRadius: 6,
                  padding: '0.7rem 0.85rem',
                }}
              >
                <strong style={{ color: 'var(--green)', display: 'block', marginBottom: 2 }}>{a.title}</strong>
                <div style={{ fontSize: '0.94rem', lineHeight: 1.5, color: 'var(--ink)' }}>{a.body}</div>
              </li>
            ))}
          </ul>
        </section>
      ))}

      <p
        style={{
          fontSize: '0.82rem',
          color: 'var(--ink-mid)',
          lineHeight: 1.5,
          margin: 'var(--sp-md) 0 0',
        }}
      >
        Page anchors on the calendar month \u2014 no manual update needed for
        the season to switch. Content is reviewed twice a year (start of
        summer and start of winter).
      </p>
    </main>
  );
}
